import React, { useEffect, useState } from 'react';
import { ShieldCheck, Lock, Eye, UserCheck, FileLock, Server } from 'lucide-react';
import { api } from '../lib/api';

export default function Compliance() {
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function load() {
            try {
                const r = await api('/system-policies');
                setPolicy(r.current);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        }
        load();
    }, []);

    if (loading) return <div className="page-center">Loading...</div>;

    return (
        <div className="compliance-page">
            <section className="compliance-header">
                <h1>HIPAA Compliance & Safeguards</h1>
                <p>Technical and administrative safeguards implemented within the PHP/eMAR system to support HIPAA-aligned data protection.</p>
            </section>

            <div className="compliance-grid">
                <div className="compliance-card">
                    <div className="card-icon"><Lock size={24}/></div>
                    <h3>Access Control</h3>
                    <ul>
                        <li><strong>Role-Based Access (RBAC):</strong> Strict permission matrix ensures users only access data required for their role.</li>
                        <li><strong>Portal Separation:</strong> Separate administrative and clinical portals to prevent unauthorized PHI access.</li>
                        <li><strong>Secure Authentication:</strong> Multifactor authentication (MFA) supported for all workforce accounts.</li>
                    </ul>
                </div>

                <div className="compliance-card">
                    <div className="card-icon"><Eye size={24}/></div>
                    <h3>Audit & Transparency</h3>
                    <ul>
                        <li><strong>Immutable Audit Logs:</strong> Every read, write, and sign action is journaled with a timestamp and user ID.</li>
                        <li><strong>Version History:</strong> All clinical note changes are versioned; signed records are immutable.</li>
                        <li><strong>Access Tracking:</strong> Platform support access is granted only via temporary, reasoned, and audited grants.</li>
                    </ul>
                </div>

                <div className="compliance-card">
                    <div className="card-icon"><Server size={24}/></div>
                    <h3>Data Isolation</h3>
                    <ul>
                        <li><strong>Tenant Isolation:</strong> Database queries are strictly scoped by facility ID to prevent cross-tenant leaks.</li>
                        <li><strong>Secure Documents:</strong> Uploads are scanned for malware and served via secure, non-public endpoints.</li>
                        <li><strong>Encryption:</strong> Data is encrypted in transit via TLS and at rest where configured by the infrastructure.</li>
                    </ul>
                </div>

                <div className="compliance-card">
                    <div className="card-icon"><UserCheck size={24}/></div>
                    <h3>Administrative Controls</h3>
                    <ul>
                        <li><strong>Least Privilege:</strong> Super-admins manage system settings but are restricted from accessing individual patient PHI.</li>
                        <li><strong>Session Management:</strong> Strict session timeouts and secure cookie attributes (HttpOnly, SameSite=Strict).</li>
                        <li><strong>Server-Side Validation:</strong> All authorizations are verified on the server, not just the UI.</li>
                    </ul>
                </div>
            </div>

            <section className="compliance-footer">
                <div className="compliance-disclaimer">
                    <h3>Compliance Notice</h3>
                    <p>The PHP/eMAR system is designed to support HIPAA-aligned safeguards. Final compliance depends on customer configuration, internal facility policies, vendor agreements, and ongoing risk assessments. This system is a tool to assist in compliance; it does not certify the facility as HIPAA compliant.</p>
                </div>
            </section>
        </div>
    );
}
